import React, { useState, useContext } from 'react';
import { DataList, Loader, CustomModal, ModalContext, AppContext, formatListData } from 'commons-ui';
import Accordian from 'commons-ui/src/components/keyValueView/Accordian';
import { Button, KIND, SIZE } from 'baseui/button';
import usePermission from 'commons-ui/src/permission/usePermission';

import useUserProfileActions from 'pages/userProfile/useUserProfileActions';
import { LIST_ENTITY_NAMES, ACTION, PAGE_NAMES, PAGE_RESOURCE_MAP } from 'constant';
import { UserDetailsContext } from 'context/userDetails';

import useStyles from '../../../basicDetails/useStyles';
import { makeActionHandler } from '../actions/handlers';
import { makeActionList } from '../actions/list';
import { columnConfigs } from './listConfig';
import useVpaDetails from './useVpaDetails';
import useService from '../useService';
import ViewPayoutRecords from '../../ViewPayoutRecords';
import { DETAIL_TYPE_UPI } from '../../../basicDetails/constants';

const VpaDetails: React.FC<{}> = () => {
	const { getActionHandler, getActionList } = useUserProfileActions(makeActionHandler, makeActionList, {
		isVpa: true,
		isBank: false
	});
	const actions = getActionList();
	const {
		vpaDetailsIsCollapsed,
		setVpaDetailsIsCollapsed,
		showLoader,
		handleDatalistFetchData,
		dataFetchComplete,
		updateDetails,
		triggerRefresh,
		dataListKey
	} = useVpaDetails();
	const { hasPermission } = usePermission();
	const { showAlert } = useContext(AppContext);
	const { styledHeaderContainer, styledHeaderTitle } = useStyles();
	const {
		selectedUser: { uid }
	} = useContext(UserDetailsContext);
	const { setModalConfig } = useContext(ModalContext);
	const { viewPayoutRecords } = useService();

	const [isViewLoading, setIsViewLoading] = useState(false);
	const [hasViewed, setHasViewed] = useState(false);

	const hasViewPermission = hasPermission({
		configPageName: PAGE_NAMES.userProfile,
		configResourceName: PAGE_RESOURCE_MAP[PAGE_NAMES.userProfile].kycDetails,
		configActionName: ACTION.viewPayoutRecords
	});

	const handleViewClick = () => {
		if (hasViewPermission) {
			setModalConfig({
				isOpen: true,
				header: 'View VPA Payout Records',
				content: (
					<ViewPayoutRecords
						loading={isViewLoading}
						onSubmit={async (ticketId: string, description?: string) => {
							setIsViewLoading(true);
							try {
								const result = await viewPayoutRecords({
									section: DETAIL_TYPE_UPI,
									uid: parseInt(uid, 10),
									ticketId,
									description
								});

								if (result.success) {
									updateDetails(formatListData(result.data || []));
									triggerRefresh();
									setVpaDetailsIsCollapsed(false);
									setHasViewed(true);
									setModalConfig({ isOpen: false });
								}
							} catch (error: any) {
								showAlert(error?.message || 'Failed to fetch payout records', { type: 'error' });
							} finally {
								setIsViewLoading(false);
							}
						}}
						onCancel={() => {
							setModalConfig({ isOpen: false });
						}}
					/>
				)
			});
		}
	};

	const vpaDetailsLabel = (
		<div className={styledHeaderContainer}>
			<div className={styledHeaderTitle}>VPA Details</div>
			{hasViewPermission && !hasViewed && (
				<Button
					kind={KIND.primary}
					size={SIZE.mini}
					onClick={(e) => {
						e.stopPropagation();
						handleViewClick();
					}}>
					View
				</Button>
			)}
		</div>
	);

	return (
		<>
			{showLoader && <Loader />}
			<Accordian
				label={vpaDetailsLabel}
				panelKey='unnamedPanel'
				isCollapsed={vpaDetailsIsCollapsed}
				setIsCollapsed={setVpaDetailsIsCollapsed}>
				{dataFetchComplete && (
					<DataList
						key={dataListKey}
						fetchData={handleDatalistFetchData}
						columnConfigs={columnConfigs}
						getActionHandler={getActionHandler}
						actions={actions}
						entity={LIST_ENTITY_NAMES.VPA_DETAILS}
						height='calc(100vh - 500px)'
						filterable={false}
						searchable={false}
						disablePagination
						showFooterMessage={false}
					/>
				)}
			</Accordian>
			<CustomModal />
		</>
	);
};

export default VpaDetails;
